import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AReport, RegularisationStatusWire, XZReport } from '@efacture/fiscal-core';
import { NEVER_QUEUED, sendBillingMutation } from '../../lib/billing-mutations';
import type { BillingTransport } from '../../billing-host/types';
import { useBillingApi } from '../../billing-host/api';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export type {
  AReport,
  ArticleSummary,
  CategorySummary,
  InvoiceTypeSummary,
  PaymentMethodSummary,
  ReportPeriod,
  TaxGroupSummary,
  XZReport,
  XZReportType,
} from '@efacture/fiscal-core';

/**
 * A closed day (§4.1). `date` is the CLOSING KEY, not the covered period: the
 * window actually covered is `summary.period`, and a skipped day is absorbed
 * by the next closing.
 */
export interface ZReportResult {
  id: string;
  date: string;
  closedAt: string;
  closedBy: string;
  invoiceCount: number;
  totalHT: string;
  totalTVA: string;
  totalTTC: string;
  firstInvoice: string | null;
  lastInvoice: string | null;
  summary: XZReport;
  mcfTransaction?: { transactionId: number; timestamp: string; synced: boolean };
}

export interface ZReportListParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
}

export interface ZReportListResponse {
  reports: ZReportResult[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

/**
 * Mirror of `formatFECExportResult` (api reports.service.ts). It sends
 * `createdAt`/`completedAt`, never a `generatedAt`, and its counts are
 * nullable while an export is still running.
 */
export interface FECExportResult {
  id: string;
  fiscalYear: number;
  filename: string;
  downloadUrl: string | null;
  recordCount: number | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  errorMessage: string | null;
  createdAt: string;
  createdBy: string;
  completedAt: string | null;
}

/**
 * A row of « À régulariser » (`GET /api/reports/a-regulariser`): a
 * provisional document, its outage, its deadline and where its
 * regularisation stands (arrêté n°2025-0049, art. 6 g)).
 */
export interface ProvisionalDocumentRow {
  id: string;
  number: string;
  type: string;
  signedAt: string;
  regularisationDueAt: string;
  status: RegularisationStatusWire;
  statusLabel: string;
  clientName: string | null;
  currency: string;
  /** Tax included, as the server stores it (a decimal string). */
  totalTTC: string;
  establishment: { code: string | null; name: string } | null;
  outage: {
    id: string;
    deviceNim: string;
    cause: string;
    startedAt: string;
    declaredAt: string;
    endedAt: string | null;
  } | null;
  /** The signed regularisation; null while none is signed. */
  regularisation: { id: string; number: string; signedAt: string } | null;
}

/** One keyset page of « À régulariser »; `nextCursor` is null on the last one. */
/** The statuses of a provisional document with no signed regularisation: the route's `status` filter. */
export type OpenRegularisationStatus = 'A_REGULARISER' | 'EN_RETARD';

export interface ProvisionalDocumentsPage {
  documents: ProvisionalDocumentRow[];
  nextCursor: string | null;
}

/** Rows asked per page of « À régulariser » (the route allows 200). */
export const PROVISIONAL_DOCUMENTS_PAGE_SIZE = 50;

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Report amounts travel as decimal strings ("12000", "-2360"). */
export { toNum } from '../../lib/decimal';

/**
 * Whether a stored summary carries the §4.3 content. A Z closed before the
 * current contract holds the previous flat shape — no `byInvoiceType`, no
 * `period` — which the declared type cannot express, so the value is widened
 * before the check.
 */
export function hasSpecContent(summary: XZReport | undefined | null): boolean {
  const widened = summary as { byInvoiceType?: unknown } | undefined | null;
  return Array.isArray(widened?.byInvoiceType);
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const reportKeys = {
  all: ['reports'] as const,
  xDaily: () => [...reportKeys.all, 'x-daily'] as const,
  xPeriodic: (from: string, to: string) => [...reportKeys.all, 'x-periodic', from, to] as const,
  aReport: () => [...reportKeys.all, 'a'] as const,
  zReports: () => [...reportKeys.all, 'z'] as const,
  zReportsList: (params: ZReportListParams) => [...reportKeys.zReports(), 'list', params] as const,
  zReportDetail: (id: string) => [...reportKeys.zReports(), 'detail', id] as const,
  fecExports: () => [...reportKeys.all, 'fec'] as const,
  fecExport: (id: string) => [...reportKeys.fecExports(), id] as const,
  provisionalDocuments: (outageId: string | null, limit: number, status: OpenRegularisationStatus | null) =>
    [...reportKeys.all, 'a-regulariser', outageId, limit, status] as const,
};

// ─────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────

async function fetchXDailyReport(api: BillingTransport): Promise<XZReport> {
  return api.get<XZReport>('/reports/x-daily');
}

async function fetchXPeriodicReport(api: BillingTransport, from: string, to: string): Promise<XZReport> {
  return api.get<XZReport>('/reports/x-periodic', { from, to });
}

async function fetchProvisionalDocuments(
  api: BillingTransport,
  outageId: string | null,
  limit: number,
  status: OpenRegularisationStatus | null,
  after: string | undefined
): Promise<ProvisionalDocumentsPage> {
  return api.get<ProvisionalDocumentsPage>('/reports/a-regulariser', {
    limit,
    after,
    ...(outageId === null ? {} : { outageId }),
    ...(status === null ? {} : { status }),
  });
}

/** Downloads the CSV of every provisional document, or of one outage's. */
export async function downloadProvisionalDocumentsCsv(api: BillingTransport, outageId: string | null): Promise<void> {
  const query = outageId === null ? '' : `&outageId=${encodeURIComponent(outageId)}`;
  await api.download(`/reports/a-regulariser?format=csv${query}`, 'documents-a-regulariser.csv');
}

async function fetchAReport(api: BillingTransport): Promise<AReport> {
  return api.get<AReport>('/reports/a');
}

async function issueAReport(api: BillingTransport): Promise<AReport> {
  return sendBillingMutation<AReport>(api, 'POST', '/reports/a', {});
}

async function fetchZReports(api: BillingTransport, params: ZReportListParams): Promise<ZReportListResponse> {
  return api.get<ZReportListResponse>('/reports/z', {
    page: params.page,
    limit: params.limit,
    startDate: params.startDate,
    endDate: params.endDate,
  });
}

async function fetchZReportDetail(api: BillingTransport, id: string): Promise<ZReportResult> {
  return api.get<ZReportResult>(`/reports/z/${id}`);
}

async function closeDay(api: BillingTransport): Promise<ZReportResult> {
  return sendBillingMutation<ZReportResult>(api, 'POST', '/reports/z');
}

async function exportFEC(api: BillingTransport, fiscalYear: number): Promise<FECExportResult> {
  return sendBillingMutation<FECExportResult>(api, 'POST', '/reports/fec', { fiscalYear });
}

async function fetchFECExportStatus(api: BillingTransport, id: string): Promise<FECExportResult> {
  return api.get<FECExportResult>(`/reports/fec/${id}`);
}

// ─────────────────────────────────────────────────────────────
// Printed documents (§2.1.c)
// ─────────────────────────────────────────────────────────────

/** Daily X as a PDF. GET /api/reports/x-daily/pdf */
async function downloadXDailyPdf(api: BillingTransport, generatedAt: string): Promise<void> {
  await api.download('/reports/x-daily/pdf', `rapport-x-quotidien-${generatedAt.slice(0, 10)}.pdf`);
}

/** Periodic X as a PDF over the same range as the screen. */
async function downloadXPeriodicPdf(api: BillingTransport, from: string, to: string): Promise<void> {
  await api.download(
    `/reports/x-periodic/pdf?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    `rapport-x-periodique-${from}-${to}.pdf`
  );
}

/**
 * A closing as a PDF. Requires `reports:close`, and the route refuses (409) a
 * closing stored without the §4.3.a-f identification block.
 */
async function downloadZReportPdf(api: BillingTransport, id: string, date: string): Promise<void> {
  await api.download(`/reports/z/${id}/pdf`, `rapport-z-${date}.pdf`);
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

/**
 * Daily X report (§4.2): the period elapsed since the last Z, read without
 * closing. The window is resolved by the server; there is no date parameter.
 * GET /api/reports/x-daily
 */
export function useXDailyReport(options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: reportKeys.xDaily(),
    queryFn: () => fetchXDailyReport(api),
    staleTime: 1000 * 60 * 2,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Periodic X report (§4.2) over a user-defined range of whole days.
 * GET /api/reports/x-periodic
 */
export function useXPeriodicReport(
  from: string,
  to: string,
  options?: { enabled?: boolean }
) {
  const api = useBillingApi();
  return useQuery({
    queryKey: reportKeys.xPeriodic(from, to),
    queryFn: () => fetchXPeriodicReport(api, from, to),
    staleTime: 1000 * 60 * 2,
    enabled: (options?.enabled ?? true) && !!from && !!to,
  });
}

/**
 * A report (§5) since the previous issued one, as a preview: reading it does
 * not move the window (`issuanceId` stays null).
 * GET /api/reports/a
 */
export function useAReport(options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: reportKeys.aReport(),
    queryFn: () => fetchAReport(api),
    staleTime: 1000 * 60 * 2,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch list of Z-Reports (closed days)
 * GET /api/reports/z
 */
export function useZReports(params: ZReportListParams = {}, options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: reportKeys.zReportsList(params),
    queryFn: () => fetchZReports(api, params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch a specific Z-Report detail
 * GET /api/reports/z/:id
 */
export function useZReportDetail(id: string, options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: reportKeys.zReportDetail(id),
    queryFn: () => fetchZReportDetail(api, id),
    staleTime: 1000 * 60 * 10, // 10 minutes (Z-reports don't change)
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/**
 * Hook to check FEC export status
 * GET /api/reports/fec/:id
 */
export function useFECExportStatus(id: string, options?: { enabled?: boolean; refetchInterval?: number }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: reportKeys.fecExport(id),
    queryFn: () => fetchFECExportStatus(api, id),
    staleTime: 1000 * 10, // 10 seconds
    enabled: (options?.enabled ?? true) && !!id,
    ...(options?.refetchInterval !== undefined
      ? { refetchInterval: options.refetchInterval }
      : {}),
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

/**
 * Hook to close the day (Z-Report)
 * POST /api/reports/z
 */
export function useCloseDay() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: () => closeDay(api),
    onSuccess: () => {
      // Invalidate Z-reports list
      queryClient.invalidateQueries({ queryKey: reportKeys.zReports() });
      // Invalidate daily X report as the day is now closed
      queryClient.invalidateQueries({ queryKey: reportKeys.xDaily() });
    },
  });
}

/**
 * Issues the A report: records the issuance, which closes the current window
 * and starts the next one. Requires `reports:close`.
 * POST /api/reports/a
 */
export function useIssueAReport() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: () => issueAReport(api),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.aReport() });
    },
  });
}

/**
 * Hook to export the FEC (Fichier des Écritures Comptables)
 * POST /api/reports/fec
 */
export function useExportFEC() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: (fiscalYear: number) => exportFEC(api, fiscalYear),
    onSuccess: () => {
      // Invalidate FEC exports list
      queryClient.invalidateQueries({ queryKey: reportKeys.fecExports() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────

export {
  fetchXDailyReport,
  fetchXPeriodicReport,
  fetchAReport,
  issueAReport,
  fetchZReports,
  fetchZReportDetail,
  closeDay,
  downloadXDailyPdf,
  downloadXPeriodicPdf,
  downloadZReportPdf,
  exportFEC,
  fetchFECExportStatus,
};

/**
 * GET /api/reports/a-regulariser — « À régulariser », page after page on the
 * server's keyset cursor; `outageId` restricts it to one outage.
 */
export function useProvisionalDocuments(
  outageId: string | null,
  options?: { limit?: number; enabled?: boolean; status?: OpenRegularisationStatus }
) {
  const api = useBillingApi();
  const limit = options?.limit ?? PROVISIONAL_DOCUMENTS_PAGE_SIZE;
  const status = options?.status ?? null;
  return useInfiniteQuery({
    queryKey: reportKeys.provisionalDocuments(outageId, limit, status),
    queryFn: ({ pageParam }) => fetchProvisionalDocuments(api, outageId, limit, status, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: options?.enabled ?? true,
  });
}
